// src/admin/AdminServiziCatalogo.jsx
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { listCategorie, listCatalogoServizi } from "./adminApi";
import "./AdminServiziCatalogo.css";

export default function AdminServiziCatalogo() {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");

  const [list, setList] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [catalogo, setCatalogo] = useState([]);

  const emptyForm = {
    descrizione: "",
    descrizione_estesa: "",
    immagine: "",
    id_categoria_servizio: "",
  };
  const [form, setForm] = useState(emptyForm);

  const [imageBusy, setImageBusy] = useState({ create: false, edit: false });
  const [imageErrors, setImageErrors] = useState({ create: "", edit: "" });

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);

  // UI: filtro + sort
  const [catFilter, setCatFilter] = useState("");
  const [sortBy, setSortBy] = useState("descrizione");
  const [sortDir, setSortDir] = useState("asc");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const r = await fetch(`${API_BASE}/api/admin/serviziadmin/servizi`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Fetch non ok");
      const js = await r.json();
      setList(Array.isArray(js) ? js : []);
    } catch {
      toast.error("Errore caricamento articoli");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const cats = await listCategorie(token);
        setCategorie(Array.isArray(cats) ? cats : []);
        const cat = await listCatalogoServizi(token);
        setCatalogo(Array.isArray(cat) ? cat : []);
      } catch {
        toast.error("Errore caricamento categorie/catalogo");
      }
    })();
  }, [token]);

  const uploadArticleImage = async (event, context) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || imageBusy[context]) return;
    const setError = message => setImageErrors(errors => ({ ...errors, [context]: message }));
    setError("");
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 3 * 1024 * 1024) {
      setError("Scegli JPG, PNG o WebP, massimo 3 MB.");
      return;
    }
    setImageBusy(busy => ({ ...busy, [context]: true }));
    try {
      const body = new FormData();
      body.append("image", file);
      const response = await fetch(`${API_BASE}/api/admin/serviziadmin/servizi/immagine`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || "Caricamento immagine non riuscito");
      if (!result.immagine || !result.immagine.startsWith("https://")) throw new Error("URL immagine non valido");
      const updateForm = context === "create" ? setForm : setEditForm;
      updateForm(current => ({ ...current, immagine: result.immagine }));
      toast.success("Immagine caricata. Salva l'articolo per associarla.");
    } catch (error) {
      setError(error.message || "Caricamento immagine non riuscito");
    } finally {
      setImageBusy(busy => ({ ...busy, [context]: false }));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (imageBusy.create) return;
    if (!form.descrizione.trim()) return toast.error("Descrizione obbligatoria");
    if (form.id_categoria_servizio === "" || form.id_categoria_servizio == null)
      return toast.error("Categoria obbligatoria");

    const payload = {
      ...form,
      id_categoria_servizio: Number(form.id_categoria_servizio),
    };

    try {
      const r = await fetch(`${API_BASE}/api/admin/serviziadmin/servizi`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!r.ok) return toast.error("Errore creazione");
      toast.success("Creato");
      setForm({ ...emptyForm });
      await load();
    } catch {
      toast.error("Errore creazione");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare l'articolo dal catalogo?")) return;
    try {
      const r = await fetch(`${API_BASE}/api/admin/serviziadmin/servizi/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return toast.error("Errore eliminazione");
      toast.success("Eliminato");
      await load();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  // inline edit
  const startEdit = (s) => {
    if (imageBusy.edit) return;
    setImageErrors(errors => ({ ...errors, edit: "" }));
    setEditingId(s.id);
    setEditForm({
      descrizione: s.descrizione || "",
      descrizione_estesa: s.descrizione_estesa || "",
      immagine: s.immagine || "",
      id_categoria_servizio:
        s.id_categoria_servizio == null ? "" : Number(s.id_categoria_servizio),
    });
  };
  const cancelEdit = () => {
    if (imageBusy.edit) return;
    setEditingId(null);
    setEditForm({ ...emptyForm });
  };
  const saveEdit = async (id) => {
    if (imageBusy.edit) return;
    if (!editForm.descrizione.trim()) return toast.error("Descrizione obbligatoria");
    if (editForm.id_categoria_servizio === "" || editForm.id_categoria_servizio == null)
      return toast.error("Categoria obbligatoria");

    const payload = {
      ...editForm,
      id_categoria_servizio: Number(editForm.id_categoria_servizio),
    };

    try {
      const r = await fetch(`${API_BASE}/api/admin/serviziadmin/servizi/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!r.ok) return toast.error("Errore salvataggio modifica");
      toast.success("Modificato");
      setEditingId(null);
      setEditForm({ ...emptyForm });
      await load();
    } catch {
      toast.error("Errore salvataggio modifica");
    }
  };

  const catLabel = (id) => {
    if (id == null || id === "") return "—";
    const c = categorie.find((x) => Number(x.id) === Number(id));
    return c ? c.descrizione : id;
  };

  // filtro + sort
  const filtered = useMemo(() => {
    if (!catFilter) return list;
    const cid = Number(catFilter);
    return list.filter((x) => Number(x.id_categoria_servizio) === cid);
  }, [list, catFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === "desc" ? -1 : 1;

    const get = (row) => {
      if (sortBy === "categoria")
        return String(catLabel(row.id_categoria_servizio || ""));
      if (sortBy === "descrizione") return String(row.descrizione || "");
      if (sortBy === "descrizione_estesa")
        return String(row.descrizione_estesa || "");
      if (sortBy === "immagine") return String(row.immagine || "");
      return String(row.descrizione || "");
    };

    arr.sort((a, b) => {
      const va = get(a).toLowerCase();
      const vb = get(b).toLowerCase();
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [filtered, sortBy, sortDir, categorie]);

  const hasRows = sorted.length > 0;

  function toggleSort(colKey) {
    if (sortBy === colKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(colKey);
      setSortDir("asc");
    }
  }

  const SortHeader = ({ k, children, className = "" }) => (
    <button
      type="button"
      className={`adm-thbtn ${className} ${sortBy === k ? "is-active" : ""}`}
      onClick={() => toggleSort(k)}
      aria-sort={
        sortBy === k ? (sortDir === "asc" ? "ascending" : "descending") : "none"
      }
      title={`Ordina per ${children}`}
    >
      <span>{children}</span>
      <span className="adm-thbtn-caret" aria-hidden>
        {sortBy === k ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
      </span>
    </button>
  );

  return (
    <div className="adm-page adm-article-catalog">
      <header className="adm-header">
        <h1>catalogo_articoli</h1>
      </header>

      {/* Solo create (il filtro categoria è nello header tabella) */}
      <section className="adm-filters">
        <div className="adm-filterbar">
          <form onSubmit={create} className="adm-createform">
            <input
              className="adm-input"
              placeholder="Descrizione *"
              value={form.descrizione}
              onChange={(e) =>
                setForm((f) => ({ ...f, descrizione: e.target.value }))
              }
            />
            <div className="adm-article-image" aria-busy={imageBusy.create}>
              <div className="adm-article-image-controls">
                <input className="adm-input" value={form.immagine} aria-label="URL immagine articolo" disabled={imageBusy.create}
                  onChange={event => setForm(current => ({ ...current, immagine: event.target.value }))} placeholder="URL immagine" />
                {form.immagine && <img key={form.immagine} src={form.immagine} alt="Anteprima articolo" className="adm-imgthumb" onError={event => { event.currentTarget.style.visibility = "hidden"; }} />}
                <label className={`adm-article-upload ${imageBusy.create ? "is-busy" : ""}`}>
                  {imageBusy.create ? "Caricamento…" : "Carica"}
                  <input type="file" accept="image/jpeg,image/png,image/webp" aria-label="Carica o sostituisci immagine articolo" disabled={imageBusy.create} onChange={event => uploadArticleImage(event, "create")} />
                </label>
              </div>
              {imageErrors.create && <span className="adm-article-image-error" role="alert">{imageErrors.create}</span>}
            </div>
            <select
              className="adm-input"
              value={form.id_categoria_servizio}
              onChange={(e) => {
                const val = e.target.value;
                setForm((f) => ({
                  ...f,
                  id_categoria_servizio: val ? Number(val) : "",
                }));
              }}
              required
            >
              <option value="">— Seleziona una categoria —</option>
              {categorie.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.descrizione}
                </option>
              ))}
            </select>
            <button type="submit" className="adm-btn adm-btn--cta" disabled={imageBusy.create}>
              <span className="adm-ico" aria-hidden>
                ➕
              </span>
            </button>
          </form>
        </div>
      </section>

      <main className="adm-main">
        <div className="adm-table" role="table" aria-label="Catalogo articoli">
          {/* head */}
          <div className="adm-row adm-row--head" role="row">
            <div className="adm-cell" role="columnheader">
              <SortHeader k="descrizione">Descrizione</SortHeader>
            </div>
            <div className="adm-cell" role="columnheader">
              <SortHeader k="descrizione_estesa">Descrizione estesa</SortHeader>
            </div>

            {/* Categoria con filtro inline */}
            <div className="adm-cell" role="columnheader">
              <div className="adm-th-stack">
                <SortHeader k="categoria">Categoria</SortHeader>
                <select
                  value={catFilter}
                  onChange={(e) => setCatFilter(e.target.value)}
                  className="adm-inhead-filter"
                  aria-label="Filtra per categoria"
                >
                  <option value="">Tutte</option>
                  {categorie.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.descrizione}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="adm-cell adm-ta-center" role="columnheader">
              <SortHeader k="immagine" className="adm-thbtn--center">
                Immagine
              </SortHeader>
            </div>
            <div className="adm-cell adm-ta-center" role="columnheader">
              Azioni
            </div>
          </div>

          {/* skeleton */}
          {loading &&
            Array.from({ length: 8 }).map((_, i) => (
              <div
                className="adm-row adm-skel"
                key={`sk-${i}`}
                role="row"
                aria-busy="true"
              >
                <div className="adm-cell">.</div>
                <div className="adm-cell">.</div>
                <div className="adm-cell">.</div>
                <div className="adm-cell adm-ta-center">.</div>
                <div className="adm-cell adm-ta-center">.</div>
              </div>
            ))}

          {/* empty */}
          {!loading && !hasRows && (
            <div className="adm-empty">Nessun articolo trovato.</div>
          )}

          {/* rows */}
          {!loading &&
            hasRows &&
            sorted.map((s) => {
              const isEditing = editingId === s.id;
              return (
                <div
                  className={`adm-row ${isEditing ? "is-editing" : ""}`}
                  role="row"
                  key={s.id}
                >
                  {/* Descrizione */}
                  <div className="adm-cell" role="cell" data-label="Descrizione">
                    {isEditing ? (
                      <input
                        className="adm-inrow-input"
                        value={editForm.descrizione}
                        onChange={(e) =>
                          setEditForm((f) => ({
                            ...f,
                            descrizione: e.target.value,
                          }))
                        }
                        placeholder="Descrizione *"
                      />
                    ) : (
                      <div className="adm-strong">{s.descrizione}</div>
                    )}
                  </div>

                  {/* Descrizione estesa */}
                  <div
                    className="adm-cell"
                    role="cell"
                    data-label="Descrizione estesa"
                  >
                    {isEditing ? (
                      <textarea
                        className="adm-inrow-textarea"
                        value={editForm.descrizione_estesa}
                        onChange={(e) =>
                          setEditForm((f) => ({
                            ...f,
                            descrizione_estesa: e.target.value,
                          }))
                        }
                        placeholder="Descrizione estesa"
                      />
                    ) : s.descrizione_estesa ? (
                      <div className="adm-muted">{s.descrizione_estesa}</div>
                    ) : (
                      "—"
                    )}
                  </div>

                  {/* Categoria */}
                  <div className="adm-cell" role="cell" data-label="Categoria">
                    {isEditing ? (
                      <select
                        className="adm-inrow-select"
                        value={editForm.id_categoria_servizio}
                        onChange={(e) => {
                          const val = e.target.value;
                          setEditForm((f) => ({
                            ...f,
                            id_categoria_servizio: val ? Number(val) : "",
                          }));
                        }}
                        required
                      >
                        <option value="">— Seleziona una categoria —</option>
                        {categorie.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.descrizione}
                          </option>
                        ))}
                      </select>
                    ) : (
                      catLabel(s.id_categoria_servizio)
                    )}
                  </div>

                  {/* Immagine */}
                  <div
                    className="adm-cell adm-ta-center"
                    role="cell"
                    data-label="Immagine"
                  >
                    {isEditing ? (
                      <div className="adm-article-image" aria-busy={imageBusy.edit}>
                        <div className="adm-article-image-controls">
                          <input className="adm-inrow-input" value={editForm.immagine} aria-label="URL immagine articolo" disabled={imageBusy.edit}
                            onChange={event => setEditForm(current => ({ ...current, immagine: event.target.value }))} placeholder="URL immagine" />
                          {editForm.immagine && <img key={editForm.immagine} src={editForm.immagine} alt="Anteprima articolo" className="adm-imgthumb" onError={event => { event.currentTarget.style.visibility = "hidden"; }} />}
                          <label className={`adm-article-upload ${imageBusy.edit ? "is-busy" : ""}`}>
                            {imageBusy.edit ? "Caricamento…" : "Carica"}
                            <input type="file" accept="image/jpeg,image/png,image/webp" aria-label="Carica o sostituisci immagine articolo" disabled={imageBusy.edit} onChange={event => uploadArticleImage(event, "edit")} />
                          </label>
                        </div>
                        {imageErrors.edit && <span className="adm-article-image-error" role="alert">{imageErrors.edit}</span>}
                      </div>
                    ) : s.immagine ? (
                      <img key={s.immagine} src={s.immagine} alt="" className="adm-imgthumb" onError={event => { event.currentTarget.style.visibility = "hidden"; }} />
                    ) : (
                      "—"
                    )}
                  </div>

                  {/* Azioni */}
                  <div
                    className="adm-cell adm-ta-center adm-actions"
                    role="cell"
                    data-label="Azioni"
                  >
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          className="adm-btn adm-iconbtn"
                          onClick={() => saveEdit(s.id)}
                          disabled={imageBusy.edit}
                          title="Salva"
                          aria-label="Salva"
                        >
                          <span className="adm-ico" aria-hidden>
                            💾
                          </span>
                        </button>{" "}
                        <button
                          type="button"
                          className="adm-btn adm-iconbtn"
                          onClick={cancelEdit}
                          disabled={imageBusy.edit}
                          title="Annulla"
                          aria-label="Annulla"
                        >
                          <span className="adm-ico" aria-hidden>
                            ↩️
                          </span>
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="adm-btn adm-iconbtn"
                          onClick={() => startEdit(s)}
                          disabled={imageBusy.edit}
                          title="Modifica"
                          aria-label="Modifica"
                        >
                          <span className="adm-ico" aria-hidden>
                            ✏️
                          </span>
                        </button>{" "}
                        <button
                          type="button"
                          className="adm-btn adm-iconbtn"
                          onClick={() => remove(s.id)}
                          disabled={imageBusy.edit}
                          title="Elimina"
                          aria-label="Elimina"
                        >
                          <span className="adm-ico" aria-hidden>
                            🗑
                          </span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </main>
    </div>
  );
}
